import { createSelector } from '@reduxjs/toolkit';
import SearchLandingPage from '../models/content-page';

import type { ContentPage, ContentPageNavigationItem } from '../@types/ContentPage';
import type { RootState } from '../reducers';

import QueryFilter, { queryString } from '../helpers/query-filter';

const emptyDocumentList: ContentPage[] = [];

export const getContentPage = createSelector(
  (state: RootState) =>
    state.contentpages.documentList ? state.contentpages.documentList.documents : emptyDocumentList,
  (state: RootState, slug: string) =>
    state.contentpages.single
      ? state.contentpages.single.documents
          .filter((page) => page.content_type === 'contentPage')
          .find((d) => d.slug === slug)
      : null,
  (state: RootState) => state.routing.path,
  (_: RootState, slug: string) => slug,
  (state: RootState) => state.contentpages.navigation,
  (pages, page, path, slug, navItems) => {
    if (page) {
      return page;
    }
    if (!navItems) {
      return pages.find((p) => p.slug === slug);
    }
    const navItem = navItems.find((item) => item.url === path);
    return navItem ? pages.find((p) => p.id === navItem.id) : null;
  },
);

export const getLandingPage = createSelector(
  (state: RootState) => state.contentpages.documentList,
  (state: RootState) => state.contentpages.single,
  (_: RootState, slug: string) => slug,
  (documentList, documentListSingles, slug) => {
    if (documentListSingles && documentListSingles.documents) {
      const singlesMatch = (documentListSingles.documents || []).find((d) => d.slug === slug);

      if (singlesMatch) return SearchLandingPage.parse(singlesMatch);
    }

    if (documentList && documentList.documents) {
      const match = (documentList.documents || []).find((p) => p.slug === slug);

      if (match) return SearchLandingPage.parse(match);
    }

    return null;
  },
);

export const isLandingPage = createSelector(
  getLandingPage,
  (contentPage) =>
    SearchLandingPage.onlyLandingPage(contentPage) ||
    SearchLandingPage.onlyContentPageWithSearch(contentPage),
);

export const isGuaranteePackageLandingPage = createSelector(getLandingPage, (contentPage) =>
  SearchLandingPage.onlyGuaranteePackageLandingPage(contentPage),
);

export const getShouldHideAds = createSelector(getLandingPage, (contentPage) => {
  if (!contentPage) {
    return true;
  }

  return contentPage.shouldHideAds;
});

export const getLandingPageQueryFilter = createSelector(
  (state: RootState) => state.contentpages.documentList,
  (state: RootState, slug: string) =>
    state.contentpages.single
      ? state.contentpages.single.documents.find((d) => d.slug === slug)
      : null,
  (_: RootState, slug: string) => slug,
  (pages, page, slug) => {
    if (!page) {
      page = pages && pages.documents ? pages.documents.find((p) => p.slug === slug) : null;
    }

    if (page && page.relatedSearch) {
      const qf = queryString('qf', page.relatedSearch);
      const queryFilter = QueryFilter.fromRepresentation(qf);

      return queryFilter.toString();
    }

    return null;
  },
);

export const getContentPageNavigation = createSelector(
  (state: RootState) => state.routing.path,
  (state: RootState) => state.contentpages.navigation,
  (path, items): ContentPageNavigationItem[] | null => {
    if (!items) {
      return null;
    }
    return items.map((x) => ({
      ...x,
      isActive: path === x.url,
    }));
  },
);
